import React, { useState } from "react";
import TagButton from "../common/TagButton";
import TabSelector from "./TabSelector";
import backgroundImage from "../../assets/Common/background_01.svg";

const video1 = "https://storage.googleapis.com/think41-web-new/assets/offeringsA/CAI.mp4"  ;
const video2 = "https://storage.googleapis.com/think41-web-new/assets/offeringsA/Dev41%20demo%20video%20(1).mp4" ;
const video3 = "https://storage.googleapis.com/think41-web-new/assets/offeringsA/Deloitte%20Booth-%20R41%20Deloitte%20(1).mp4" ;

const tabsMockData = [
  {
    name: "CAI",
    description:
      "AI agents that automate product development—from spoken ideas to deployable code and self-healing test flows.",
    functions: [
      {
        name: "Copilots for Agents",
        description:
          "Real-time assistance during calls—retrieving knowledge, auto-summarizing conversations, and suggesting responses on the fly.",
      },
      {
        name: "Adaptive + Multilingual Personas",
        description:
          "Agents personalize responses based on user tone, behavior, and language—creating seamless, human-like interactions across regions.",
      },
      {
        name: "VAD + Turn Detection",
        description:
          "Understands when to speak and when to listen. Accurately detects speaker shifts, pauses, and intent for smoother conversations.",
      },
    ],
    metrices: [
      {
        value: "< 500ms",
        description: "Ultra low response time for real-time conversations",
      },
      {
        value: "100K+",
        description:
          "Concurrent conversations handled without compromising performance",
      },
      {
        value: "End-to-End Encryption",
        description:
          "Secure enterprise-grade communication across every interaction",
      },
    ],
    video: (
      <video
        src={video1}
        width="640"
        height="480"
        allow="autoplay"
        muted
        controls
        className="w-full h-full object-cover aspect-video"
      ></video>
    ),
  },
  {
    name: "Dev41",
    description:
      "AI agents that automate product development—from spoken ideas to deployable code and self-healing test flows.",
    functions: [
      {
        name: "Jotter",
        description:
          "Jotter intelligently understands user conversations and creates well-defined requirements with illustrated concepts.",
      },
      {
        name: "Mapper",
        description:
          "Mapper transforms the requirements into executable project plans and helps you navigate the software development process on autopilot.",
      },
      {
        name: "Forger",
        description:
          "Forger builds, tests, deploys, and iterates over version-controlled applications, always ready for production-scale deployment.",
      },
    ],
    metrices: [
      {
        value: "50%",
        description: "Reduction in requirement gaps and revision cycles",
      },
      {
        value: "40%",
        description: "Faster development with fewer bugs in initial code",
      },
      {
        value: "80%",
        description:
          "Drop in deployment issues through AI-led testing & automation",
      },
    ],
    video: (
      <video
        src={video2}
        width="640"
        height="480"
        allow="autoplay"
        muted
        controls
        className="w-full h-full object-cover aspect-video"
      ></video>
    ),
  },
  {
    name: "Recruit41",
    description:
      "AI agents that automate product development—from spoken ideas to deployable code and self-healing test flows.",
    functions: [
      {
        name: "Automated Screening at Scale",
        description:
          "Run thousands of AI-led interviews simultaneously—with structured evaluations for both technical and behavioral skills.",
      },
      {
        name: "AI-Powered Coding & Evaluations",
        description:
          "Assess candidates using customized coding challenges and real-world case studies, scored autonomously and fairly.",
      },
      {
        name: "Flexible Scheduling & Support",
        description:
          "Candidates interview at their convenience with real-time system support—ensuring smooth participation across regions.",
      },
    ],
    metrices: [
      {
        value: "10,000+",
        description: "Candidates screened across 26 colleges in 5 days",
      },
      {
        value: "1,141",
        description: "Concurrent interviews handled with no quality drop",
      },
      {
        value: "Weeks -> Days",
        description: "AI automation reduced evaluation time dramatically",
      },
    ],
    video: (
      <video
        src={video3}
        width="640"
        height="480"
        allow="autoplay"
        className="w-full h-full object-cover aspect-video"
        muted
        controls
      ></video>
    ),
  },
];

const SolutionAccelerator = () => {
  const tabs = tabsMockData.map((tab) => tab.name);
  const [selected, setSelected] = useState("Dev41");
  const [selectedIndex, setSelectedIndex] = useState(1);

  return (
    <div
      className="w-full bg-cover bg-no-repeat overflow-hidden pt-[36px]"
      id="ai_platform"
      style={{
        backgroundImage: `url(${backgroundImage})`,
      }}
    >
      <div className="flex flex-col gap-[36px] md:max-w-[1440px] w-full pt-[35px] md:pt-[50px] px-[20px] md:px-[120px] mx-auto items-center md:items-start">
        <div className="w-full items-start">
          <TagButton text={"Solution Accelerators"} />
        </div>
        <div className="flex flex-col gap-[27px] items-center md:items-start">
          <div className="bg-clip-text text-[24px] md:text-[40px] font-semibold leading-[32px] md:leading-[53.24px] font-montserrat inline-block w-fit text-white">
            Flexible And Scalable Platforms
          </div>
          <TabSelector
            tabs={tabs}
            selected={selected}
            setSelected={setSelected}
            setSelectedIndex={setSelectedIndex}
          />
          <div className="md:w-[524px] text-white font-roboto text-base font-light leading-[25px] md:text-start">
            {tabsMockData[selectedIndex].description}
          </div>
        </div>
        <div className="flex flex-col-reverse md:flex-row gap-[24px] mb-[80px] w-full">
          <div className="space-y-4">
            {tabsMockData[selectedIndex].functions.map((func, idx) => (
              <div
                key={idx}
                className="relative bg-white rounded-sm md:rounded-[8px] border border-[#727272] w-full md:w-[502px] md:h-[150px] flex-shrink-0 p-4 shadow-sm flex flex-col gap-2 overflow-hidden"
              >
                <div className="w-[140%] h-[120%] top-[-63px] right-[0px] absolute bg-[#E9EEFC] -rotate-[11deg] z-[-10] "></div>
                <h3 className="text-[#282828] font-semibold text-[20px] leading-[32px] not-italic font-montserrat border-b border-[#444] pb-1">
                  {func.name}
                </h3>
                <p className="text-[#444] font-light text-[16px] leading-[20px] md:leading-[25px] font-roboto">
                  {func.description}
                </p>
              </div>
            ))}
          </div>
          <div className="w-full md:max-w-full h-[220px] md:w-[674px] md:h-[482px] flex-shrink-0 rounded-[2.674px] bg-[#E9EEFC]">
            {tabsMockData[selectedIndex].video}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SolutionAccelerator;
 
